import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsEvent {
  event_type: 'started' | 'step_viewed' | 'completed' | 'skipped' | 'paused' | 'error';
  step_number?: number;
  step_id?: string;
  error_message?: string;
  metadata?: Record<string, any>;
}

export const useTourAnalytics = (userId: string | undefined) => {
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const tourStartTimeRef = useRef<number | null>(null);
  const lastStepTimeRef = useRef<number | null>(null);

  const trackAnalyticsEvent = useCallback(async (event: AnalyticsEvent) => {
    try {
      const now = Date.now();
      
      // Track tour start time
      if (event.event_type === 'started') {
        tourStartTimeRef.current = now;
        lastStepTimeRef.current = now;
      }

      // Calculate durations
      const duration_ms = lastStepTimeRef.current 
        ? now - lastStepTimeRef.current 
        : null;
      
      const total_elapsed_ms = tourStartTimeRef.current 
        ? now - tourStartTimeRef.current 
        : null;

      // Update last step time
      if (event.event_type === 'step_viewed') {
        lastStepTimeRef.current = now;
      }

      // Insert analytics event
      const { error } = await supabase
        .from('tour_analytics')
        .insert({
          user_id: userId || null,
          session_id: sessionIdRef.current,
          event_type: event.event_type,
          step_number: event.step_number,
          step_id: event.step_id,
          duration_ms,
          total_elapsed_ms,
          error_message: event.error_message,
          metadata: event.metadata || {},
        });

      if (error) {
        console.error('Error tracking tour analytics:', error);
      }
    } catch (error) {
      console.error('Exception tracking tour analytics:', error);
    }
  }, [userId]);

  return { trackAnalyticsEvent };
};